/**
 * 基于 @improbable-eng/grpc-web 的 Connect Transport 实现
 * 兼容 @connectrpc/connect 的 Transport 接口，复用现有拦截器与配置
 * 支持超时、取消、默认实例导出，便于真实接口联调
 */

import type {
  DescMessage,
  DescMethodStreaming,
  MessageInitShape,
  MessageShape,
} from '@bufbuild/protobuf'
import { create, fromBinary, toBinary } from '@bufbuild/protobuf'
import type { ContextValues, StreamResponse, Transport } from '@connectrpc/connect'
import { Code, ConnectError } from '@connectrpc/connect'
import { grpc } from '@improbable-eng/grpc-web'
import type { ProtobufMessage, ProtobufMessageClass } from '@improbable-eng/grpc-web/dist/typings/message'
import { BrowserHeaders } from 'browser-headers'

import { AuthReplaySession } from '@/services/auth-replay'
import { transport } from '@/services/api/client'
import { apiConfig } from '@/services/api/config'
import { useAuthStore } from '@/stores/auth'

// ========== 认证配置接口 ==========

/**
 * 传输层认证配置，由调用方注入，解除与 Pinia store 的耦合。
 */
export interface TransportAuthConfig {
  /** 获取当前内存中的 Access Token */
  getAccessToken: () => string | null
  /** 触发 token 刷新（含去重），返回新 token */
  onTokenExpired: () => Promise<string>
  /** 无需认证重试的方法名集合（默认包含 Login、RefreshTokens、Logout） */
  skipAuthMethods?: Set<string>
}

/** 默认跳过认证重试的方法白名单 */
const DEFAULT_SKIP_AUTH_METHODS: ReadonlySet<string> = new Set([
  'Login',
  'RefreshTokens',
  'Logout',
])

// ========== gRPC 状态码枚举 ==========

/**
 * 本地定义 gRPC-Web 状态码，因为 @improbable-eng/grpc-web 未导出该枚举。
 */
enum GrpcWebCode {
  OK = 0,
  CANCELED = 1,
  UNKNOWN = 2,
  INVALID_ARGUMENT = 3,
  DEADLINE_EXCEEDED = 4,
  NOT_FOUND = 5,
  ALREADY_EXISTS = 6,
  PERMISSION_DENIED = 7,
  RESOURCE_EXHAUSTED = 8,
  FAILED_PRECONDITION = 9,
  ABORTED = 10,
  OUT_OF_RANGE = 11,
  UNIMPLEMENTED = 12,
  INTERNAL = 13,
  UNAVAILABLE = 14,
  DATA_LOSS = 15,
  UNAUTHENTICATED = 16,
}

// ========== 类型定义 ==========

interface GrpcWebTransportOptions {
  baseUrl: string
  authConfig: TransportAuthConfig
  withCredentials?: boolean
}

type GrpcMethodDef = grpc.MethodDefinition<ProtobufMessage, ProtobufMessage>

type GrpcClient = grpc.Client<ProtobufMessage, ProtobufMessage>

/** 流消息队列项：数据、结束标记或错误 */
type StreamQueueItem<O> =
  | { value: O; done?: undefined; error?: undefined }
  | { done: true; value?: undefined; error?: undefined }
  | { error: Error; value?: undefined; done?: undefined }

// ========== 工具函数 ==========

function isValidHeaderName(name: string): boolean {
  return /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/.test(name)
}

/** 安全添加 HTTP Header，忽略非法名称 */
function safeAppendHeader(headers: Headers, key: string, value: string): void {
  if (!key || !isValidHeaderName(key)) {
    return
  }
  try {
    headers.append(key, value)
  } catch {
    // 忽略无效 header，避免中断流处理
  }
}

/** 安全关闭 gRPC 客户端，忽略重复关闭错误 */
function safeCloseGrpcClient(client: GrpcClient | null | undefined): void {
  if (!client) {
    return
  }
  try {
    client.close()
  } catch {
    // 忽略 grpc-web client 竞态导致的重复关闭错误
  }
}

/**
 * 将 BrowserHeaders 的键值对复制到标准 Headers 对象。
 *
 * BrowserHeaders.forEach 的回调签名为 (key: string, values: string[])，
 * 每个 key 对应一个 string[] 数组。
 */
function copyBrowserHeadersToHeaders(source: BrowserHeaders, target: Headers): void {
  source.forEach((key: string, values: string[]) => {
    for (const val of values) {
      safeAppendHeader(target, key, val)
    }
  })
}

/**
 * 将 @connectrpc 的 DescMethodStreaming 转为 @improbable-eng/grpc-web 的 MethodDefinition。
 *
 * requestType / responseType 仅实现了 deserializeBinary，
 * 因为 grpc-web client 只在接收响应时调用该方法。构造函数和 toObject 不会被使用，
 * 所以此处使用类型断言将部分实现的对象视为 ProtobufMessageClass。
 */
function descMethodToGrpcStreamingMethodDef<I extends DescMessage, O extends DescMessage>(
  method: DescMethodStreaming<I, O>,
): GrpcMethodDef {
  return {
    methodName: method.name,
    service: { serviceName: method.parent.typeName },
    requestStream:
      method.methodKind === 'client_streaming' ||
      method.methodKind === 'bidi_streaming',
    responseStream:
      method.methodKind === 'server_streaming' ||
      method.methodKind === 'bidi_streaming',
    // grpc-web 仅在反序列化响应时调用 deserializeBinary，此处提供最小实现。
    // ProtobufMessageClass 要求 new() 构造函数，但 grpc-web client 内部不会调用它，
    // 因此使用 as unknown as 桥接两个不兼容的类型系统。
    requestType: {
      deserializeBinary: (bytes: Uint8Array): MessageShape<I> =>
        fromBinary(method.input, bytes) as MessageShape<I>,
    } as unknown as ProtobufMessageClass<ProtobufMessage>,
    responseType: {
      deserializeBinary: (bytes: Uint8Array): MessageShape<O> =>
        fromBinary(method.output, bytes) as MessageShape<O>,
    } as unknown as ProtobufMessageClass<ProtobufMessage>,
  }
}

/**
 * gRPC-Web Code -> Connect Code 映射。
 * Connect Code 与 gRPC Code 数值一致，直接返回。
 */
function mapGrpcCodeToConnect(code: number): Code {
  return code as Code
}

// ========== 流式连接内部类型 ==========

/** createStreamPromise 所需的全部参数 */
interface StreamParams<I extends DescMessage, O extends DescMessage> {
  host: string
  method: DescMethodStreaming<I, O>
  methodDef: GrpcMethodDef
  shouldSkipAuthRetry: boolean
  header: HeadersInit | undefined
  input: AsyncIterable<MessageInitShape<I>>
  signal: AbortSignal | undefined
  timeoutMs: number | undefined
  transportFactory: grpc.TransportFactory
  authConfig: TransportAuthConfig
}

/** 流连接的可变状态 */
interface StreamState<O> {
  queue: Array<StreamQueueItem<O>>
  notifyQueue: (() => void) | null
  streamError: Error | null
  streamDone: boolean
  headersReceived: boolean
  responseMessageDelivered: boolean
  inputFinished: boolean
  activeClientAttempt: number
  currentClient: GrpcClient | null
  responseTrailer: Headers
  bufferedRequests: Uint8Array[]
  authReplaySession: AuthReplaySession
}

function createInitialStreamState<O>(authReplaySession: AuthReplaySession): StreamState<O> {
  return {
    queue: [],
    notifyQueue: null,
    streamError: null,
    streamDone: false,
    headersReceived: false,
    responseMessageDelivered: false,
    inputFinished: false,
    activeClientAttempt: 0,
    currentClient: null,
    responseTrailer: new Headers(),
    bufferedRequests: [],
    authReplaySession,
  }
}

// ========== 流式连接辅助函数 ==========

/** 向队列推送一条消息并唤醒等待的消费者 */
function pushToQueue<O>(state: StreamState<O>, item: StreamQueueItem<O>): void {
  state.queue.push(item)
  if (state.notifyQueue) {
    const notify = state.notifyQueue
    state.notifyQueue = null
    notify()
  }
}

/** 标记流失败并通知消费者 */
function failStream<O>(
  state: StreamState<O>,
  err: Error,
  reject: (reason: Error) => void,
): void {
  if (state.streamDone) return
  state.streamError = err
  state.streamDone = true
  pushToQueue(state, { error: err })
  if (!state.headersReceived) {
    reject(err)
  }
}

/**
 * 构建 gRPC 请求元数据（BrowserHeaders），包含认证 token 和自定义 header。
 */
function buildMetadata(
  authConfig: TransportAuthConfig,
  header: HeadersInit | undefined,
  tokenOverride?: string,
): BrowserHeaders {
  const metadata = new BrowserHeaders()
  const token = tokenOverride ?? authConfig.getAccessToken()

  if (token) {
    metadata.append('authorization', `Bearer ${token}`)
    metadata.append('x-authorization', `Bearer ${token}`)
  }

  if (!header) return metadata

  if (header instanceof Headers) {
    header.forEach((value, key) => {
      if (key) metadata.append(key, value)
    })
  } else if (Array.isArray(header)) {
    header.forEach(([k, v]) => {
      if (k) metadata.append(k, v)
    })
  } else if (typeof header === 'object') {
    Object.entries(header).forEach(([k, v]) => {
      if (k) metadata.append(k, v as string)
    })
  }

  return metadata
}

/** 序列化请求消息为 Uint8Array */
function serializeRequest<I extends DescMessage>(
  method: DescMethodStreaming<I, DescMessage>,
  req: MessageInitShape<I>,
): Uint8Array {
  const reqMsg = create(method.input, req)
  return toBinary(method.input, reqMsg)
}

/** 向 grpc-web client 发送已序列化的请求字节 */
function sendSerializedRequest(client: GrpcClient, reqBytes: Uint8Array): void {
  const wrapper: ProtobufMessage = {
    serializeBinary: () => reqBytes,
    toObject: () => ({}),
  }
  client.send(wrapper)
}

/** 创建异步响应迭代器，用于消费流中的消息 */
function createResponseIterator<O>(state: StreamState<O>): AsyncGenerator<O, void, undefined> {
  async function* iterator(): AsyncGenerator<O, void, undefined> {
    while (true) {
      if (state.queue.length > 0) {
        const item = state.queue.shift()!
        if (item.error) throw item.error
        if (item.done) return
        yield item.value!
      } else {
        if (state.streamDone) {
          if (state.streamError) throw state.streamError
          return
        }
        await new Promise<void>((r) => (state.notifyQueue = r))
      }
    }
  }
  return iterator()
}

// ========== gRPC 客户端创建与事件处理 ==========

/**
 * 启动 gRPC-Web 客户端连接并绑定事件回调。
 */
function startClient<I extends DescMessage, O extends DescMessage>(
  params: StreamParams<I, O>,
  state: StreamState<MessageShape<O>>,
  resolve: (response: StreamResponse<I, O>) => void,
  reject: (reason: Error) => void,
  responseIterator: AsyncGenerator<MessageShape<O>, void, undefined>,
  tokenOverride?: string,
  replayFrom = 0,
): void {
  const { host, method, methodDef, shouldSkipAuthRetry, header, signal, transportFactory, authConfig } = params

  if (state.streamDone || signal?.aborted) return

  const metadata = buildMetadata(authConfig, header, tokenOverride)
  const client = grpc.client(methodDef, {
    host,
    transport: transportFactory,
  })
  state.activeClientAttempt += 1
  const currentAttempt = state.activeClientAttempt
  state.currentClient = client

  // 收到响应 Header 时 resolve 外层 Promise
  client.onHeaders((headers) => {
    if (currentAttempt !== state.activeClientAttempt || state.headersReceived) return
    state.headersReceived = true
    const respHeaders = new Headers()
    copyBrowserHeadersToHeaders(headers, respHeaders)
    resolve({
      stream: true,
      service: method.parent,
      method,
      header: respHeaders,
      message: responseIterator,
      trailer: state.responseTrailer,
    })
  })

  // grpc-web 的 onMessage 回调声明类型为 ProtobufMessage，但实际值由
  // responseType.deserializeBinary 返回，已经是 MessageShape<O>。
  // 两个类型系统在结构上无重叠，需要通过 unknown 中转进行断言。
  client.onMessage((msg: ProtobufMessage) => {
    if (currentAttempt !== state.activeClientAttempt || state.streamDone) return
    state.responseMessageDelivered = true
    pushToQueue(state, { value: msg as unknown as MessageShape<O> })
  })

  // 流结束处理：正常结束、错误、或认证重试
  client.onEnd((code, msg, trailers) => {
    handleStreamEnd(params, state, resolve, reject, responseIterator, client, currentAttempt, code, msg, trailers)
  })

  client.start(metadata)

  // 重放缓冲的请求
  for (let i = replayFrom; i < state.bufferedRequests.length; i += 1) {
    sendSerializedRequest(client, state.bufferedRequests[i])
  }
  if (state.inputFinished) {
    client.finishSend()
  }
}

/**
 * 处理流结束事件：成功、失败或触发认证重试。
 */
function handleStreamEnd<I extends DescMessage, O extends DescMessage>(
  params: StreamParams<I, O>,
  state: StreamState<MessageShape<O>>,
  resolve: (response: StreamResponse<I, O>) => void,
  reject: (reason: Error) => void,
  responseIterator: AsyncGenerator<MessageShape<O>, void, undefined>,
  client: GrpcClient,
  currentAttempt: number,
  code: grpc.Code,
  msg: string,
  trailers: BrowserHeaders,
): void {
  if (currentAttempt !== state.activeClientAttempt || state.streamDone) return

  if (trailers) {
    copyBrowserHeadersToHeaders(trailers, state.responseTrailer)
  }

  if (Number(code) === GrpcWebCode.OK) {
    state.streamDone = true
    pushToQueue(state, { done: true })
    return
  }

  const { shouldSkipAuthRetry, authConfig } = params
  const errorCode = mapGrpcCodeToConnect(Number(code))
  const err = new ConnectError(msg || 'gRPC Error', errorCode)
  const canAuthRetry = !state.responseMessageDelivered
    && !shouldSkipAuthRetry
    && errorCode === Code.Unauthenticated

  if (canAuthRetry) {
    try {
      state.authReplaySession.recordUnauthorizedAndAssertCanReplay({
        code: errorCode,
        cause: err,
      })
    } catch (limitError) {
      failStream(state, limitError instanceof Error ? limitError : err, reject)
      return
    }

    // gRPC 客户端流的认证重试会创建全新的连接，不能把它当作原连接的续传。
    // 新流必须从首帧 metadata 开始完整重放，否则后端会收到空流或缺少首帧的非法流。
    const nextReplayFrom = 0
    void state.authReplaySession.refreshAuthorization(authConfig.onTokenExpired)
      .then((newToken) => {
        if (state.streamDone || currentAttempt !== state.activeClientAttempt) return
        safeCloseGrpcClient(client)
        state.currentClient = null
        startClient(params, state, resolve, reject, responseIterator, newToken, nextReplayFrom)
      })
      .catch((refreshErr: unknown) => {
        const error = refreshErr instanceof Error
          ? refreshErr
          : new Error('Access Token 刷新失败')
        failStream(state, error, reject)
      })
    return
  }

  failStream(state, err, reject)
}

// ========== 输入消息处理 ==========

/**
 * 启动异步输入泵：消费输入迭代器，缓冲并发送请求。
 */
function runInputPump<I extends DescMessage, O extends DescMessage>(
  params: StreamParams<I, O>,
  state: StreamState<MessageShape<O>>,
  onStartClient: () => void,
  reject: (reason: Error) => void,
): void {
  const { method, methodDef, signal, input } = params

  const bufferRequest = (req: MessageInitShape<I>) => {
    const reqBytes = serializeRequest(method, req)
    state.bufferedRequests.push(reqBytes)
    if (state.currentClient) {
      sendSerializedRequest(state.currentClient, reqBytes)
    }
  }

  const pumpInput = async (prebufferBeforeStart: boolean) => {
    let sentCount = 0
    for await (const req of input) {
      if (signal?.aborted || state.streamDone) break
      bufferRequest(req)
      sentCount += 1
    }
    if (!methodDef.requestStream && sentCount === 0 && !signal?.aborted && !state.streamDone) {
      throw new ConnectError(
        `${method.parent.typeName}.${method.name} 缺少请求消息`,
        Code.Internal,
      )
    }
    state.inputFinished = true
    state.currentClient?.finishSend()
    if (prebufferBeforeStart && !state.streamDone && !signal?.aborted) {
      onStartClient()
    }
  }

  void (async () => {
    try {
      await pumpInput(!methodDef.requestStream)
    } catch (err: unknown) {
      safeCloseGrpcClient(state.currentClient)
      const error = err instanceof Error ? err : new Error('流请求发送失败')
      failStream(state, error, reject)
    }
  })()
}

// ========== 超时与取消 ==========

/** 设置超时定时器，超时后关闭客户端并终止流 */
function setupTimeout<O>(
  timeoutMs: number | undefined,
  state: StreamState<O>,
  reject: (reason: Error) => void,
): void {
  if (timeoutMs == null || timeoutMs <= 0) return

  setTimeout(() => {
    if (!state.streamDone) {
      safeCloseGrpcClient(state.currentClient)
      failStream(
        state,
        new ConnectError('Timeout', mapGrpcCodeToConnect(GrpcWebCode.DEADLINE_EXCEEDED)),
        reject,
      )
    }
  }, timeoutMs)
}

/** 监听 AbortSignal，收到中止信号后关闭客户端并终止流 */
function setupAbortHandler<O>(
  signal: AbortSignal | undefined,
  state: StreamState<O>,
  reject: (reason: Error) => void,
): void {
  if (!signal) return

  signal.addEventListener('abort', () => {
    safeCloseGrpcClient(state.currentClient)
    failStream(
      state,
      new ConnectError('Aborted', mapGrpcCodeToConnect(GrpcWebCode.CANCELED)),
      reject,
    )
  })
}

// ========== 流式连接核心逻辑 ==========

/**
 * 创建流式连接的 Promise，将 gRPC-Web 的回调风格 API 包装为 Connect StreamResponse。
 */
function createStreamPromise<I extends DescMessage, O extends DescMessage>(
  params: StreamParams<I, O>,
): Promise<StreamResponse<I, O>> {
  const { methodDef, signal, timeoutMs } = params
  const state = createInitialStreamState<MessageShape<O>>(new AuthReplaySession({
    requestKind: 'stream',
    operation: `${params.method.parent.typeName}.${params.method.name}`,
  }))

  return new Promise<StreamResponse<I, O>>((resolve, reject) => {
    const responseIterator = createResponseIterator(state)

    const doStartClient = (tokenOverride?: string, replayFrom?: number) => {
      startClient(params, state, resolve, reject, responseIterator, tokenOverride, replayFrom)
    }

    // 双向流/客户端流：立即启动连接
    if (methodDef.requestStream) {
      doStartClient()
    }

    // 异步消费输入流
    runInputPump(params, state, () => doStartClient(), reject)

    // 超时与取消处理
    setupTimeout(timeoutMs, state, reject)
    setupAbortHandler(signal, state, reject)
  })
}

// ========== Connect Transport 实现 ==========

export function createGrpcWebTransport(opts: GrpcWebTransportOptions): Transport {
  const { baseUrl, authConfig } = opts
  const transportFactory = grpc.WebsocketTransport()
  const skipAuthMethods = authConfig.skipAuthMethods ?? DEFAULT_SKIP_AUTH_METHODS

  const grpcWebTransport: Transport = {
    // 复用已有的 unary 实现
    unary: transport.unary,

    // 流式传输实现
    stream<I extends DescMessage, O extends DescMessage>(
      method: DescMethodStreaming<I, O>,
      signal: AbortSignal | undefined,
      timeoutMs: number | undefined,
      header: HeadersInit | undefined,
      input: AsyncIterable<MessageInitShape<I>>,
      _contextValues?: ContextValues,
    ): Promise<StreamResponse<I, O>> {
      const host = baseUrl.replace(/\/$/, '')
      const methodDef = descMethodToGrpcStreamingMethodDef(method)
      const shouldSkipAuthRetry = skipAuthMethods.has(method.name)

      return createStreamPromise({
        host,
        method,
        methodDef,
        shouldSkipAuthRetry,
        header,
        input,
        signal,
        timeoutMs,
        transportFactory,
        authConfig,
      })
    },
  }

  return {
    unary: grpcWebTransport.unary,
    stream: grpcWebTransport.stream,
  }
}

// ========== 基础 URL 规范化 ==========

/**
 * gRPC-Web WebSocket 需要 http(s) 协议；将相对路径或 ws(s) 转换为 http(s)。
 */
function normalizeGrpcWebBaseUrl(baseUrl: string): string {
  if (baseUrl.startsWith('/')) {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}${baseUrl}`
    }
    return baseUrl
  }
  if (baseUrl.startsWith('ws://')) {
    return baseUrl.replace('ws://', 'http://')
  }
  if (baseUrl.startsWith('wss://')) {
    return baseUrl.replace('wss://', 'https://')
  }
  return baseUrl
}

// ========== Token 刷新去重 ==========

/**
 * 创建带去重的 Token 刷新回调。
 * 多个并发流同时触发刷新时，只执行一次实际刷新请求。
 */
function createDedupedTokenRefresh(
  refreshFn: () => Promise<string>,
): () => Promise<string> {
  let refreshPromise: Promise<string> | null = null
  return () => {
    if (!refreshPromise) {
      refreshPromise = refreshFn().finally(() => {
        refreshPromise = null
      })
    }
    return refreshPromise
  }
}

// ========== 默认实例 ==========

/**
 * 默认的 gRPC-Web Transport 实例。
 * 通过 Pinia Store 回调注入认证逻辑，Transport 层本身不直接依赖 Store。
 */
export const grpcWebTransport = createGrpcWebTransport({
  baseUrl: normalizeGrpcWebBaseUrl(apiConfig.baseUrl),
  withCredentials: true,
  authConfig: {
    getAccessToken: () => useAuthStore().getAccessToken(),
    onTokenExpired: createDedupedTokenRefresh(() => useAuthStore().refreshSession()),
  },
})
